/**
 * Local benchmark OHLC — SPY and a distinct QQQ path (hop 9).
 */
import type {
  BenchmarkOhlcQuery,
  BenchmarkOhlcResult,
  BenchmarkSymbol,
  IndexCode,
  OhlcCoverage,
  OhlcSeries,
  Ticker,
} from '@meridian/contracts';

import {
  BENCHMARK_PRICE,
  BENCHMARK_TICKER,
  QQQ_BENCHMARK_PRICE,
  QQQ_BENCHMARK_TICKER,
} from '../../constants/benchmark';
import { syntheticOhlcFromCloses, toOhlcSeries } from '../../series/synthetic-ohlc';
import { syntheticSessions } from '../../series/synthetic-sessions';

import { indexBenchmarkCloses } from './benchmark-series';

import type { DerivedSecurity } from '../../universe/derive-security';

function envelopeCloses(
  ticker: Ticker,
  window: BenchmarkOhlcQuery['window'],
  closes: readonly number[],
): OhlcSeries {
  return toOhlcSeries(
    ticker,
    window,
    syntheticSessions(closes.length),
    syntheticOhlcFromCloses(closes),
  );
}

/**
 * The synthetic path per served symbol. `satisfies Record<BenchmarkSymbol, …>`
 * is the lockstep: adding a member to `BENCHMARK_SYMBOLS` fails to typecheck
 * here until it has its own index, ticker and terminal. A bare
 * `if (symbol === 'QQQ') … else` would instead have served SPY's closes under
 * the new symbol — schema-valid, plausible, and wrong, because nothing on the
 * wire cross-checks `symbol` against `series.ticker`.
 */
const BENCHMARK_PATHS = {
  SPY: { index: 'SPX', ticker: BENCHMARK_TICKER, price: BENCHMARK_PRICE },
  QQQ: { index: 'NDX', ticker: QQQ_BENCHMARK_TICKER, price: QQQ_BENCHMARK_PRICE },
} as const satisfies Record<
  BenchmarkSymbol,
  { readonly index: IndexCode; readonly ticker: Ticker; readonly price: number }
>;

export function buildBenchmarkOhlc(
  rows: readonly DerivedSecurity[],
  query: BenchmarkOhlcQuery,
): BenchmarkOhlcResult {
  const path = BENCHMARK_PATHS[query.symbol];
  const closes = indexBenchmarkCloses(rows, query.window, path.index, path.price);
  return { status: 'available', series: envelopeCloses(path.ticker, query.window, closes) };
}

export function benchmarkOhlcFromCompleteSuffix(
  suffix: OhlcSeries | null,
  examined: OhlcCoverage | null,
): BenchmarkOhlcResult {
  if (suffix !== null) return { status: 'available', series: suffix };
  if (examined === null) {
    return { status: 'unavailable', reason: 'empty_window', coverage: null };
  }
  return { status: 'unavailable', reason: 'no_complete_ohlc', coverage: examined };
}
